import ky, { isHTTPError } from "ky";

import { AptiError } from "@/core/errors";
import { createS3FormData } from "@/lib/helpers/createS3FormData";
import { getPhotoIDFileKey, getPhotoIDFileName } from "@/core/helpers/photoID";
import type { MimeType, GetPresignedUrlForPostResponse, UUID } from "@/core/types";
import { sharedRequestsTyped } from "@/api/(requests)/sharedTyped";
import { validatePresignedResponse } from "@/core/helpers/s3";

type UploadPhotoIDParams = {
  file: File;
  mimeType: MimeType;
  patientId: UUID;
};

export async function uploadPhotoID({ file, mimeType, patientId }: UploadPhotoIDParams) {
  let presignedResponse: GetPresignedUrlForPostResponse;

  // Step 1: Get presigned URL from backend
  try {
    presignedResponse = await sharedRequestsTyped.post_presigned_url({
      data: {
        content_type: mimeType,
        entity_id: patientId,
        entity_type: "patient_personal_id_image",
        file_key: getPhotoIDFileKey({ id: patientId }),
        file_name: getPhotoIDFileName({ id: patientId }),
        method: "post_object",
      },
    });

    // Handle null/undefined response from API
    if (!presignedResponse) {
      throw new AptiError({
        code: "S3_INVALID_PRESIGNED_RESPONSE",
        message: "Backend returned empty response - this may indicate a server error",
      });
    }

    // Validate response structure before proceeding
    if (!validatePresignedResponse(presignedResponse)) {
      throw new AptiError({
        code: "S3_INVALID_PRESIGNED_RESPONSE",
        message: "Backend returned invalid presigned URL response",
      });
    }
  } catch (error) {
    // Re-throw AptiError validation errors as-is
    if (error instanceof AptiError) {
      throw error;
    }

    // Wrap other errors with context
    throw new AptiError({
      cause: error,
      code: "S3_PRESIGNED_URL_FAILED",
      message: "Failed to get presigned URL for Personal ID",
    });
  }

  // Step 2: Upload file to S3
  try {
    const formData = createS3FormData({ fields: presignedResponse.fields, file });

    // Use bare ky (no interceptors) since this posts directly to AWS, not our API
    // S3 returns 204 No Content on success — response body is empty and not consumed by callers
    await ky.post(presignedResponse.url, { body: formData, timeout: 60000 });
  } catch (error) {
    const statusInfo = isHTTPError(error) ? `(Status: ${error.response.status})` : "";

    throw new AptiError({
      cause: error,
      code: "S3_UPLOAD_FAILED",
      context: {
        fileSize: file.size,
        fileType: file.type,
      },
      message: `Failed to upload Personal ID to S3 ${statusInfo}`,
    });
  }
}
